import React, { useState, useEffect } from 'react';
import { useNotify } from 'react-admin';
import { Button as Antbutton, Form, Input } from 'antd';
import { makeStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import moment from 'moment';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import AddIcon from '@material-ui/icons/Add';

import ButtonUI from '@material-ui/core/Button';
import { apiGet, apiPost } from '../brainApi';
import { localStorageGet } from '../helper';

const useStyles = makeStyles((theme) => ({
  paperBase: {
    padding: '10px 15px',
    margin: '5px 5px 15px',
    boxShadow: 'none',
  },
  listLabel: {
    fontWeight: '600',
    color: '#000',
  },
  listColor: {
    color: '#888',
  },
  btnShadow: {
    boxShadow: '0 0 1px 0 rgb(53 64 82 / 88%)',
  },
  AlignRight: {
    textAlign: 'right',
  },
  divide: {
    marginTop: '10px',
    marginBottom: '10px',
  },
  adminInput: {
    backgroundColor: 'transparent',
    paddingRight: 15,
    '& .MuiFilledInput-inputMarginDense': {
      border: '1px solid #ccc',
      borderRadius: 30,
    },
    '& .MuiFilledInput-root': {
      backgroundColor: 'transparent',
    },
    '& .MuiFilledInput-underline': {
      '&::before': {
        borderBottom: 'none',
      },
      '&::after': {
        borderBottom: 'none',
      },
    },
  },
  infoText: {
    margin: 10,
  },
  showMore: {
    marginTop: 5,
    color: '#888',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  notesBtn: {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
    },
    '&:focus': {
      backgroundColor: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
    },
  },
  notesInput: {
    margin: '0px 15px 0px 0px',
    borderRadius: '15px',
  },
  compactNoteList: {
    textAlign: 'center',
  },
}));

const AdminNotes = ({
  selectedDate,
  service_vehicle_id,
  isCompact = false,
}) => {
  const classes = useStyles();
  const [text, setText] = useState('');
  const [adminNotes, setAdminNotes] = useState(null);
  const [showCount, setShowCount] = useState(3);
  const date = moment(selectedDate).format('YYYY-MM-DD');

  useEffect(() => {
    if (!text) {
      const params = { date_start: date, date_end: date };
      apiGet('/calendar-note', params)
        .then((json) => {
          setAdminNotes(json);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [date, selectedDate, text]);

  const filterKey = adminNotes && adminNotes[service_vehicle_id];
  const filteredNotes = filterKey && filterKey[date];
  const filteredSortedNotes = filteredNotes
    ?.sort((a, b) => new Date(b.modified_at) - new Date(a.modified_at))
    .slice(0, showCount);

  const renderNoteList = () =>
    filteredSortedNotes?.map(({ text, employee, modified_at }) => (
      <div
        key={modified_at}
        className={`${classes.listColor} ${
          isCompact ? classes.compactNoteList : ''
        }`}
      >
        &quot;{text}&quot; -{' '}
        <Tooltip
          title={
            <AdminNotesToolTip employee={employee} modified_at={modified_at} />
          }
          placement="right"
        >
          <span>{employee.first_name}</span>
        </Tooltip>
      </div>
    ));

  return (
    <>
      <Divider className={classes.divide} />
      <Grid item md={12}>
        <Box className={classes.paperBase}>
          <Box>
            <AdminNotesForm
              selectedDate={selectedDate}
              service_vehicle_id={service_vehicle_id}
              text={text}
              setText={setText}
              isCompact={isCompact}
            />
          </Box>
          <Box mt={2}>
            {filteredNotes?.length > 0
              ? renderNoteList()
              : !isCompact && <span>No notes yet!</span>}
            {showCount < filteredNotes?.length && (
              <Box
                display="flex"
                alignItems="flex-end"
                className={classes.showMore}
                onClick={() => setShowCount((prev) => prev + 3)}
              >
                <AddIcon fontSize="small" />
                <Typography variant="body2"> Show More</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Grid>
    </>
  );
};

export default AdminNotes;

const AdminNotesForm = ({
  selectedDate,
  service_vehicle_id,
  text,
  setText,
  isCompact = false,
}) => {
  const classes = useStyles();
  const { TextArea } = Input;
  const { user_id } = localStorageGet('user');
  const date = moment(selectedDate).format('YYYY-MM-DD');
  const notify = useNotify();

  const handleSubmit = () => {
    if (text) {
      const body = {
        service_vehicle_id,
        date,
        employee_id: user_id,
        text,
      };
      apiPost('/calendar-note', body)
        .then(() => {
          setText('');
          notify('Admin notes updated successfully');
        })
        .catch((error) => {
          notify(error.message, 'error');
        });
    }
  };

  if (isCompact) {
    return (
      <Form
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <TextArea
          autoSize={{ minRows: 1, maxRows: 4 }}
          placeholder="Leave day note or comment..."
          className={classes.notesInput}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <ButtonUI color="primary" onClick={handleSubmit} size="small">
          Leave Note
        </ButtonUI>
      </Form>
    );
  }

  return (
    <Form>
      <Box display="flex" alignItems="center">
        <TextArea
          autoSize={{ minRows: 1, maxRows: 4 }}
          placeholder="Leave day note or comment..."
          className={classes.notesInput}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <Antbutton
          type="primary"
          shape="round"
          onClick={handleSubmit}
          className={classes.notesBtn}
        >
          Leave Note
        </Antbutton>
      </Box>
    </Form>
  );
};

const AdminNotesToolTip = ({ employee, modified_at }) => {
  const localDateTime = moment
    .utc(modified_at)
    .local()
    .format('MM/DD/YYYY HH:mm A');
  return (
    <>
      <div>
        <span>Posted by: </span>
        <span>{`${employee.first_name} ${employee.last_name}`}</span>
      </div>
      <div>
        <span>On: </span>
        <span>{localDateTime}</span>
      </div>
    </>
  );
};
